import { useMemo } from 'react'
import type { MessageResponse } from '@/api/types_session'
import type { CitationResponse } from '@/api/types'
import { CONVERSATION_MESSAGE_TYPE, TURN_FAILURE_MESSAGE_TYPE } from '@/api/types_session'
import { MessageBubble } from './MessageBubble'
import { TypingIndicator } from './TypingIndicator'
import { LoadingSkeleton } from '@/components/clinical'
import { usePendingTurnsStore, pendingTurnsForSession } from '@/stores/pendingTurns'

interface MessageListProps {
  messages: MessageResponse[]
  isLoading: boolean
  isPending: boolean
  emptyMessage: string
  onCitationClick?: (citation: CitationResponse) => void
  onRetry?: () => void
  isRetrying?: boolean
}

/**
 * The transcript. Every row of a session's log that this surface shows is a
 * conversation message or a durable turn_failure — the session's other
 * records (effort) live on its record stream
 * upstream, not in the transcript, so nothing is filtered here on content:
 * the conversation type IS the filter, and a type this client predates is a
 * record it has no surface for.
 */
export function MessageList({
  messages,
  isLoading,
  isPending,
  emptyMessage,
  onCitationClick,
  onRetry,
  isRetrying = false,
}: MessageListProps) {
  const visibleMessages = useMemo(
    () =>
      messages.filter(
        (m) => m.type === CONVERSATION_MESSAGE_TYPE || m.type === TURN_FAILURE_MESSAGE_TYPE,
      ),
    [messages],
  )

  // The authoritative "reply pending" signal — used by the empty state to
  // show the thinking affordance instead of "no messages" while the first
  // turn is still on the worker. Cleared on turn.completed and turn.failed.
  const sessionId = messages[0]?.session ?? ''
  const hasPendingSessionTurn = usePendingTurnsStore(
    (s) => sessionId !== '' && pendingTurnsForSession(s.turns, sessionId).length > 0,
  )

  if (isLoading) {
    return (
      <div className="h-full overflow-y-auto px-4 py-4">
        <LoadingSkeleton shape="message-bubble" count={3} />
      </div>
    )
  }

  if (visibleMessages.length === 0) {
    if (hasPendingSessionTurn) {
      return (
        <div className="h-full overflow-y-auto px-4 py-4">
          <TypingIndicator />
        </div>
      )
    }
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-neutral-500 text-center px-8">
          {emptyMessage}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {visibleMessages.map((message, index) => {
        const isLatest = index === visibleMessages.length - 1
        return (
          <MessageBubble
            key={message.id}
            message={message}
            isLatest={isLatest}
            onCitationClick={onCitationClick}
            onRetry={isLatest ? onRetry : undefined}
            isRetrying={isLatest ? isRetrying : false}
          />
        )
      })}
      {isPending && <TypingIndicator />}
    </div>
  )
}
